import {
    FileInfo,
    CampaignChannel,
    CampaignInfo,
    Campaign,
    CampaignStatus,
    CampaignDateFilterBy,
    CampaignTypeFilter,
    CampaignSimpleStats,
    ExportFileFormats,
    CompressionFormat,
    ExportLink,
    JourneySummary,
    JourneyStatus,
    Journey,
    JourneyDailySummary,
    DeleteAutomationsPayload,
    AutomationContactType,
} from '@dev/ee-api/ee-api-models';
import { AxiosPromise, AxiosResponse } from 'axios';

import { GetCampaignsPayload } from '@api/store/campaigns/types';
import { request } from './request';

interface CampaignsListParams {
    search?: string;
    offset?: number;
    limit?: number;
}

export const getAllCampaigns = (params: CampaignsListParams): Promise<CampaignInfo[]> => {
    const { Campaigns } = request();

    return Campaigns.campaignsAllGet(params.search, params.offset, params.limit).then((resp): CampaignInfo[] => {
        return resp.data;
    });
};

export const loadTargetCampaigns = (name: string, nameLike: string, limit: number, offset: number): AxiosPromise<CampaignChannel[]> => {
    const { Campaigns } = request();
    return Campaigns.campaignsTargetsByTargetGet(name, nameLike, limit, offset).then((resp): AxiosResponse<CampaignChannel[]> => {
        return resp;
    });
};

export const uploadFile = (params: {
    file: File;
    expiresAfterDays?: number;
    onUpload?(params: { total?: number; loaded: number }): void;
}): Promise<FileInfo> => {
    const options = {
        onUploadProgress: params.onUpload,
    };

    const { Files } = request(options);
    return Files.filesPost(params.expiresAfterDays || 0, false, params.file).then((resp): FileInfo => resp.data);
};

export const loadFiles = (): Promise<FileInfo[]> => {
    const { Files } = request();

    return Files.filesGet().then((resp): FileInfo[] => resp.data);
};

export const deleteFile = (fileName: string): AxiosPromise<void> => {
    const { Files } = request();

    return Files.filesByFilenameDelete(fileName);
};

export const deleteFiles = (filesList: string[]): AxiosPromise<void> => {
    const { Files } = request();

    return Files.filesDeleterequestsPost(filesList);
};

export interface GetCampaignsParams {
    search?: string;
    from?: string;
    to?: string;
    status?: CampaignStatus[];
    type?: CampaignTypeFilter[];
    dateFilterBy?: CampaignDateFilterBy;
    offset?: number;
    limit?: number;
    options?: any;
}

export const getCampaigns = (params: GetCampaignsParams): AxiosPromise<Campaign[]> => {
    const { Campaigns } = request();
    const { search, from, to, status, type, dateFilterBy, offset, limit, options } = params;

    return Campaigns.campaignsGet(search, from, to, status, type, dateFilterBy, offset, limit, options);
};

export const cancelCampaign = (name: string): AxiosPromise<void> => {
    const { Campaigns } = request();

    return Campaigns.campaignsByNameCancelPost(name);
};

export const updateCampaign = (campaign: Campaign): AxiosPromise<Campaign> => {
    const { Campaigns } = request();

    return Campaigns.campaignsByNamePut(campaign.Name, campaign);
};

export const deleteCampaigns = (Names: string[]): AxiosPromise<void> => {
    const { Campaigns } = request();

    return Campaigns.campaignsDeletePost({ Names });
};

export const getSingleCampaignByName = (name: string): AxiosPromise<Campaign> => {
    const { Campaigns } = request();

    return Campaigns.campaignsByNameGet(name);
};

export type SimpleStatsCampaignParams = {
    name: string;
    from?: string;
    to?: string;
};

export const getCampaignSimpleStatsByName = (params: SimpleStatsCampaignParams): AxiosPromise<CampaignSimpleStats> => {
    const { name, from, to } = params;
    const { Campaigns } = request();

    return Campaigns.campaignsSimplestatsByNameGet(name, from, to);
};

export type ExportCampaignsParams = {
    campaignNames: string[];
    fileFormat?: ExportFileFormats;
    compressionFormat?: CompressionFormat;
    fileName?: string;
};

export const exportCampaigns = (params: ExportCampaignsParams): AxiosPromise<ExportLink> => {
    const { Campaigns } = request();
    const { campaignNames, fileFormat, compressionFormat, fileName } = params;

    return Campaigns.campaignsTasksExportextendedGet(campaignNames, fileFormat, compressionFormat, fileName);
};

export type ExportAutomationParams = {
    name: string;
    type: AutomationContactType;
    from?: string | null;
    to?: string | null;
    fileFormat?: ExportFileFormats;
    compressionFormat?: CompressionFormat;
    fileName?: string;
};

export const exportAutomationContacts = (params: ExportAutomationParams): AxiosPromise<ExportLink> => {
    const { Campaigns } = request();
    const { name, type, from, to, fileFormat, compressionFormat, fileName } = params;

    return Campaigns.campaignsAutomationByNameContactsExportGet(name, type, from, to, fileFormat, compressionFormat, fileName);
};

export const copyCampaignsByName = (name: string): AxiosPromise<Campaign> => {
    const { Campaigns } = request();

    return Campaigns.campaignsByNameCopyPost(name);
};

export type GetAutomationsParams = {
    from?: string;
    to?: string;
    statuses?: JourneyStatus[];
    name?: string;
    limit?: number;
    offset?: number;
    search?: string;
};

export const getAllAutomations = (params: GetAutomationsParams): AxiosPromise<JourneySummary[]> => {
    const { Campaigns } = request();
    const { from, to, statuses, name, limit, offset } = params;

    return Campaigns.campaignsAutomationSummaryGet(from, to, statuses, name, limit, offset);
};

export const updateAutomationStatus = (name: string, status: JourneyStatus): AxiosPromise<Journey> => {
    const { Campaigns } = request();

    return Campaigns.campaignsAutomationByNameStatusPatch(name, status);
};

export const deleteAutomation = (deleteAutomationsPayload: DeleteAutomationsPayload): AxiosPromise<void> => {
    const { Campaigns } = request();

    return Campaigns.campaignsAutomationBulkdeletePost(deleteAutomationsPayload);
};

export const getAutomationByName = (name: string): AxiosPromise<JourneySummary> => {
    const { Campaigns } = request();

    return Campaigns.campaignsAutomationByNameSummaryGet(name);
};

export type GetAutomationDailySummaryParams = {
    name: string;
    from?: string;
    to?: string;
    channelName?: string;
    dateFilter?: string;
};

export const getAutomationDailySummary = (params: GetAutomationDailySummaryParams): AxiosPromise<JourneyDailySummary[]> => {
    const { Campaigns } = request();
    const { from, to, name, channelName } = params;

    return Campaigns.campaignsAutomationByNameDailysummaryGet(name, channelName, from, to);
};

export type GetAutomationContactsParams = {
    name: string;
    type: AutomationContactType;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
};

export const getAutomationContacts = (params: GetAutomationContactsParams): AxiosPromise<string[]> => {
    const { Campaigns } = request();
    const { from, to, name, type, limit, offset } = params;

    return Campaigns.campaignsAutomationByNameContactsGet(name, type, from, to, limit, offset);
};

export const campaignsGET = ({
    search,
    from,
    to,
    status,
    type,
    dateFilterBy,
    offset,
    limit,
}: GetCampaignsPayload): AxiosPromise<Campaign[]> => {
    const { Campaigns } = request();
    return Campaigns.campaignsGet(search, from, to, status, type, dateFilterBy, offset, limit);
};
